import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, MapPin, Volume2, VolumeX } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Frontend fallback — only used if the CMS call fails. Every value is
// editable from the Admin dashboard (Admin → Home Hero) and picked up here.
const HERO_FALLBACK = {
  introEyebrow: 'A WEDDING CELEBRATION',
  introDateShort: '07 · 31 · 2027',
  coupleFirst: 'Mary',
  coupleSecond: 'Arrington',
  hashtag: '#GodBlessTheJohnsons',
  motto: 'Grace Made Us. Purpose Unites Us.',
  scripture: {
    text: '"...At the right time, I, the Lord, will make it happen."',
    reference: '— Isaiah 60:22',
  },
  dateDisplay: 'Saturday, July 31, 2027',
  weddingDateISO: '2027-07-31T15:00:00',
  ctaLabel: 'View Details',
};

const HeroIntro = ({ onComplete, cms }) => {
  return (
    <motion.div
      className="hero-intro"
      initial={{ opacity: 1 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 1.6, ease: [0.22, 1, 0.36, 1] }}
      onAnimationComplete={(def) => { if (def === 'exit' && onComplete) onComplete(); }}
      data-testid="hero-intro"
    >
      <div className="hero-intro-inner">
        <motion.span
          className="hero-intro-eyebrow"
          initial={{ opacity: 0, letterSpacing: '0.1em' }}
          animate={{ opacity: 1, letterSpacing: '0.32em' }}
          transition={{ duration: 1.4, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
        >
          {cms.introEyebrow}
        </motion.span>

        <motion.div
          className="hero-intro-line"
          initial={{ scaleX: 0 }}
          animate={{ scaleX: 1 }}
          transition={{ duration: 1.2, delay: 0.6, ease: [0.22, 1, 0.36, 1] }}
        />

        <motion.h1
          className="hero-intro-name"
          initial={{ opacity: 0, y: 18, filter: 'blur(8px)' }}
          animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
          transition={{ duration: 1.6, delay: 0.8, ease: [0.22, 1, 0.36, 1] }}
        >
          {cms.coupleFirst}
        </motion.h1>

        <motion.span
          className="hero-intro-amp"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.4, delay: 1.1, ease: [0.22, 1, 0.36, 1] }}
        >
          <span className="hero-intro-amp-flame">&amp;</span>
        </motion.span>

        <motion.h1
          className="hero-intro-name"
          initial={{ opacity: 0, y: 18, filter: 'blur(8px)' }}
          animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
          transition={{ duration: 1.6, delay: 1.4, ease: [0.22, 1, 0.36, 1] }}
        >
          {cms.coupleSecond}
        </motion.h1>

        <motion.div
          className="hero-intro-line"
          initial={{ scaleX: 0 }}
          animate={{ scaleX: 1 }}
          transition={{ duration: 1.2, delay: 1.8, ease: [0.22, 1, 0.36, 1] }}
        />

        <motion.span
          className="hero-intro-date"
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.2, delay: 2.0, ease: [0.22, 1, 0.36, 1] }}
        >
          {cms.introDateShort}
        </motion.span>
      </div>
    </motion.div>
  );
};

const HeroSection = () => {
  const navigate = useNavigate();
  const [cms, setCms] = useState(HERO_FALLBACK);
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0
  });
  const [videoLoaded, setVideoLoaded] = useState(false);
  const [videoSrc, setVideoSrc] = useState(null);
  const [showIntro, setShowIntro] = useState(() => {
    if (typeof window === 'undefined') return true;
    try {
      return sessionStorage.getItem('mw_intro_seen') !== '1';
    } catch (e) {
      return true;
    }
  });
  const videoRef = useRef(null);
  const audioRef = useRef(null);
  const audioCtxRef = useRef(null);
  const audioGainRef = useRef(null);
  const audioSourceRef = useRef(null);
  const countdownRef = useRef(null);
  const [audioOn, setAudioOn] = useState(false);
  const [audioCanPlay, setAudioCanPlay] = useState(false);
  const [pastHero, setPastHero] = useState(false);
  const [showAudioHint, setShowAudioHint] = useState(() => {
    if (typeof window === 'undefined') return false;
    try {
      return sessionStorage.getItem('mw_audio_hint_seen') !== '1';
    } catch (e) {
      return false;
    }
  });

  const weddingDate = new Date(cms.weddingDateISO || HERO_FALLBACK.weddingDateISO);

  // Fetch hero CMS content — polling + visibility-change refresh so a Save
  // in Admin shows up on every open tab within seconds without a redeploy.
  useEffect(() => {
    let alive = true;
    const fetchCms = async () => {
      try {
        const res = await axios.get(`${API}/hero/content`, { params: { _: Date.now() } });
        if (!alive || !res.data) return;
        setCms((prev) => ({ ...HERO_FALLBACK, ...res.data, scripture: { ...HERO_FALLBACK.scripture, ...(res.data.scripture || {}) } }));
      } catch { /* keep whatever we have */ }
    };
    fetchCms();
    const interval = setInterval(fetchCms, 15000);
    const onVis = () => { if (document.visibilityState === 'visible') fetchCms(); };
    document.addEventListener('visibilitychange', onVis);
    return () => { alive = false; clearInterval(interval); document.removeEventListener('visibilitychange', onVis); };
  }, []);

  // Target volume cap — keep it subtle even at full
  const AUDIO_MAX_VOL = 0.42;

  // Lazily create the Web Audio graph (iOS-safe volume control).
  // iOS Safari treats HTMLMediaElement.volume as read-only — using a GainNode
  // is the only way to fade. Must be created inside a user gesture.
  const ensureAudioGraph = () => {
    const a = audioRef.current;
    if (!a) return null;
    // Reuse a previously-built graph if the audio element already has one
    // (handles StrictMode remounts & repeated toggles)
    if (a._wedGraph) {
      audioCtxRef.current = a._wedGraph.ctx;
      audioGainRef.current = a._wedGraph.gain;
      audioSourceRef.current = a._wedGraph.source;
      return a._wedGraph;
    }
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return null;
    const ctx = new Ctx();
    let source;
    try {
      source = ctx.createMediaElementSource(a);
    } catch (e) {
      // Should not happen since we guarded above, but bail safely
      try { ctx.close(); } catch (_) { /* noop */ }
      return null;
    }
    const gain = ctx.createGain();
    gain.gain.value = 0;
    source.connect(gain).connect(ctx.destination);
    const graph = { ctx, gain, source };
    a._wedGraph = graph;
    audioCtxRef.current = ctx;
    audioGainRef.current = gain;
    audioSourceRef.current = source;
    return graph;
  };

  // Smoothly ramp the GainNode to target over `seconds` (Web Audio time domain)
  const rampGain = (targetVol, seconds = 1.4) => {
    const ctx = audioCtxRef.current;
    const gain = audioGainRef.current;
    if (!ctx || !gain) return;
    const now = ctx.currentTime;
    const current = gain.gain.value;
    gain.gain.cancelScheduledValues(now);
    gain.gain.setValueAtTime(current, now);
    gain.gain.linearRampToValueAtTime(targetVol, now + seconds);
  };

  // Toggle audio — SYNCHRONOUS inside the click event for iOS Safari trust.
  // No awaits, no setTimeouts, no async before play().
  const toggleAudio = () => {
    const a = audioRef.current;
    if (!a) return;

    // Permanently dismiss the audio hint as soon as the user notices it
    if (showAudioHint) {
      try { sessionStorage.setItem('mw_audio_hint_seen', '1'); } catch (e) { /* noop */ }
      setShowAudioHint(false);
    }

    if (!audioOn) {
      // 1) Build the Web Audio graph (must be in user gesture on iOS)
      const graph = ensureAudioGraph();
      // 2) Resume the AudioContext synchronously inside the gesture
      if (graph && graph.ctx && graph.ctx.state === 'suspended') {
        graph.ctx.resume(); // fire-and-forget; do not await
      }
      // 3) Unmute & play SYNCHRONOUSLY — this is what iOS requires
      a.muted = false;
      const playPromise = a.play();
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => { /* ignore — iOS will throw if not in gesture */ });
      }
      // 4) Ramp the gain (volume) up smoothly
      rampGain(AUDIO_MAX_VOL, 1.8);
      setAudioOn(true);
    } else {
      // Fade gain down, then pause once silent (iOS-safe — no async)
      rampGain(0, 0.9);
      const ctx = audioCtxRef.current;
      const delayMs = 950;
      // Use setTimeout (not awaited inside gesture) — OK because audio already unlocked
      window.setTimeout(() => {
        const a2 = audioRef.current;
        if (a2) a2.pause();
        if (ctx && ctx.state === 'running') {
          // keep ctx alive for fast re-play; just zero the gain
        }
      }, delayMs);
      setAudioOn(false);
    }
  };

  // Combined scroll handler:
  //  1) Fade audio out via GainNode as user scrolls past the hero (iOS safe)
  //  2) Subtle parallax on the countdown — floats up to ~6px slower than the
  //     background as the page scrolls, adding a layer of cinematic depth.
  useEffect(() => {
    let raf = null;
    const PARALLAX_FACTOR = 0.07; // 100px scroll → 7px lag
    const PARALLAX_MAX = 6;       // absolute cap (px)
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = null;
        const vh = window.innerHeight;
        const y = window.scrollY;
        const past = Math.max(0, Math.min(1, y / (vh * 0.85)));
        setPastHero(past >= 0.85);

        // Parallax: translate countdown by a fraction of scroll, capped at 6px
        const el = countdownRef.current;
        if (el) {
          const lag = Math.min(PARALLAX_MAX, y * PARALLAX_FACTOR);
          el.style.transform = `translate3d(0, ${lag.toFixed(2)}px, 0)`;
        }

        const gain = audioGainRef.current;
        const ctx = audioCtxRef.current;
        if (!gain || !ctx || !audioOn) return;
        const target = AUDIO_MAX_VOL * (1 - past);
        const now = ctx.currentTime;
        gain.gain.cancelScheduledValues(now);
        gain.gain.setValueAtTime(gain.gain.value, now);
        gain.gain.linearRampToValueAtTime(target, now + 0.25);
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [audioOn]);

  // Auto-dismiss the cinematic intro after ~5s (skips if user clicks/touches)
  useEffect(() => {
    if (!showIntro) return;
    const dismiss = () => {
      try { sessionStorage.setItem('mw_intro_seen', '1'); } catch (e) { /* noop */ }
      setShowIntro(false);
    };
    const timer = setTimeout(dismiss, 5000);
    window.addEventListener('click', dismiss);
    window.addEventListener('touchstart', dismiss, { passive: true });
    window.addEventListener('keydown', dismiss);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('click', dismiss);
      window.removeEventListener('touchstart', dismiss);
      window.removeEventListener('keydown', dismiss);
    };
  }, [showIntro]);

  // Lock body scroll while intro is visible
  useEffect(() => {
    if (showIntro) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = prev; };
    }
  }, [showIntro]);

  // Enable the audio toggle as soon as the audio element is in the DOM.
  // iOS Safari is unreliable about firing 'canplaythrough' for <audio>, so we
  // don't gate the button on it — play() will work as long as the user gestures.
  useEffect(() => {
    if (audioRef.current) setAudioCanPlay(true);
  }, []);

  // Dismiss the audio hint after a short window OR as soon as the user taps the toggle.
  // First-session only — persisted via sessionStorage.
  useEffect(() => {
    if (!showAudioHint) return;
    // Don't begin the hint until the cinematic intro is done
    if (showIntro) return;
    const dismiss = () => {
      try { sessionStorage.setItem('mw_audio_hint_seen', '1'); } catch (e) { /* noop */ }
      setShowAudioHint(false);
    };
    const timer = setTimeout(dismiss, 12000); // gentle exposure window
    return () => clearTimeout(timer);
  }, [showAudioHint, showIntro]);

  // Choose video resolution based on viewport (mobile = 540p ~7MB, desktop = 720p ~14MB)
  useEffect(() => {
    const isMobile = typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches;
    setVideoSrc(isMobile ? '/video/save-the-date-540p.mp4' : '/video/save-the-date-720p.mp4');
  }, []);

  useEffect(() => {
    const calculateTimeLeft = () => {
      const now = new Date();
      const difference = weddingDate - now;

      if (difference > 0) {
        setTimeLeft({
          days: Math.floor(difference / (1000 * 60 * 60 * 24)),
          hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
          minutes: Math.floor((difference / 1000 / 60) % 60),
          seconds: Math.floor((difference / 1000) % 60)
        });
      }
    };

    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 1000);
    return () => clearInterval(timer);
  }, []);

  // iOS Safari autoplay safety net — re-attempt play after user interaction
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;

    const tryPlay = () => {
      const p = v.play();
      if (p && typeof p.catch === 'function') p.catch(() => {});
    };

    tryPlay();

    const onFirstTouch = () => {
      tryPlay();
      window.removeEventListener('touchstart', onFirstTouch);
      window.removeEventListener('click', onFirstTouch);
    };
    window.addEventListener('touchstart', onFirstTouch, { once: true, passive: true });
    window.addEventListener('click', onFirstTouch, { once: true });

    return () => {
      window.removeEventListener('touchstart', onFirstTouch);
      window.removeEventListener('click', onFirstTouch);
    };
  }, []);

  const scrollToSection = (sectionId) => {
    document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' });
  };

  const goTo = (path) => navigate(path);

  // Editorial stagger animation
  const fadeUp = {
    hidden: { opacity: 0, y: 24, filter: 'blur(6px)' },
    show: (i = 0) => ({
      opacity: 1,
      y: 0,
      filter: 'blur(0px)',
      transition: { duration: 1.4, delay: 0.2 + i * 0.18, ease: [0.22, 1, 0.36, 1] }
    })
  };

  return (
    <section id="hero" className="hero-section" data-testid="hero-section">
      {/* Ambient cinematic audio — muted on mount, user-toggled, hero-only */}
      <audio
        ref={audioRef}
        preload="auto"
        loop
        muted
        playsInline
        data-testid="hero-audio"
      >
        {/* iOS Safari prefers AAC/m4a; mp3 is universal fallback */}
        <source src="/audio/on-this-day.m4a" type="audio/mp4" />
        <source src="/audio/on-this-day.mp3" type="audio/mpeg" />
      </audio>

      {/* Discreet sound toggle — top-right near nav, hides past hero */}
      <button
        type="button"
        onClick={toggleAudio}
        disabled={!audioCanPlay}
        className={`hero-audio-toggle ${audioOn ? 'is-on' : ''} ${pastHero ? 'is-hidden' : ''} ${showAudioHint && !showIntro ? 'is-hinting' : ''}`}
        aria-label={audioOn ? 'Mute ambient music' : 'Play ambient music'}
        aria-pressed={audioOn}
        aria-hidden={pastHero}
        tabIndex={pastHero ? -1 : 0}
        data-testid="hero-audio-toggle"
        title={audioOn ? 'Sound on — tap to mute' : 'Sound off — tap for ambient music'}
      >
        <span className="hero-audio-toggle-dot" aria-hidden="true">
          {audioOn ? <Volume2 strokeWidth={1.5} className="w-[14px] h-[14px]" /> : <VolumeX strokeWidth={1.5} className="w-[14px] h-[14px]" />}
        </span>
        <span className="hero-audio-toggle-label">
          {audioOn ? 'Sound on' : 'Sound'}
        </span>
      </button>

      {/* Cinematic 5-second intro overlay — fades to reveal the hero */}
      <AnimatePresence>
        {showIntro && <HeroIntro onComplete={() => setShowIntro(false)} cms={cms} />}
      </AnimatePresence>

      {/* Cinematic Video Background — viewport-tuned source for performance */}
      <video
        ref={videoRef}
        key={videoSrc /* re-mount if src changes */}
        className={`hero-bg-video ${videoLoaded ? 'is-loaded' : ''}`}
        src={videoSrc || undefined}
        poster="/video/poster.jpg"
        autoPlay
        muted
        loop
        playsInline
        preload="auto"
        disablePictureInPicture
        onLoadedData={() => setVideoLoaded(true)}
        onCanPlay={() => setVideoLoaded(true)}
        data-testid="hero-video"
      />
      {/* Poster shows beneath video until loaded — also iOS autoplay fallback */}
      <img
        src="/video/poster.jpg"
        alt="Mary & Arrington — Save the Date"
        className="hero-bg-poster"
        aria-hidden="true"
      />

      {/* Layered cinematic overlays — subtle, warm, editorial */}
      <div className="hero-overlay-soft" />
      <div className="hero-overlay-vignette" />
      <div className="hero-overlay-grain" />
      <div className="hero-overlay-seam" />

      {/* Content */}
      <div className="hero-content max-w-4xl mx-auto">
        {/* Editorial top hashtag — small, refined, with hairline */}
        <motion.div
          className="hero-eyebrow"
          variants={fadeUp}
          initial="hidden"
          animate="show"
          custom={0}
          data-testid="hero-hashtag"
        >
          <span className="hero-eyebrow-line" />
          <span className="hero-eyebrow-text">{cms.hashtag}</span>
          <span className="hero-eyebrow-line" />
        </motion.div>

        {/* Couple Names */}
        <motion.h1
          className="hero-name"
          variants={fadeUp}
          initial="hidden"
          animate="show"
          custom={1}
        >
          {cms.coupleFirst}
        </motion.h1>
        <motion.p
          className="hero-ampersand"
          variants={fadeUp}
          initial="hidden"
          animate="show"
          custom={2}
        >
          <span className="hero-ampersand-flame">&amp;</span>
        </motion.p>
        <motion.h1
          className="hero-name"
          variants={fadeUp}
          initial="hidden"
          animate="show"
          custom={3}
        >
          {cms.coupleSecond}
        </motion.h1>

        {/* Quote / Scripture */}
        <motion.div
          variants={fadeUp}
          initial="hidden"
          animate="show"
          custom={4}
          className="hero-quote-block"
        >
          <p className="hero-tagline">{cms.motto}</p>
          <p className="hero-scripture">
            {cms.scripture?.text}
            <span className="hero-scripture-cite">{cms.scripture?.reference}</span>
          </p>
        </motion.div>

        {/* Date with refined divider */}
        <motion.div
          variants={fadeUp}
          initial="hidden"
          animate="show"
          custom={5}
          className="hero-date-block"
        >
          <span className="hero-divider-thin" />
          <p className="hero-date">{cms.dateDisplay}</p>
          <span className="hero-divider-thin" />
        </motion.div>

        {/* Countdown — refined premium glass, subtle parallax wrapper */}
        <div ref={countdownRef} className="hero-countdown-parallax" data-testid="hero-countdown-parallax">
          <motion.div
            className="hero-countdown"
            variants={fadeUp}
            initial="hidden"
            animate="show"
            custom={6}
            data-testid="hero-countdown"
          >
          {[
            { value: timeLeft.days, label: 'Days' },
            { value: timeLeft.hours, label: 'Hours' },
            { value: timeLeft.minutes, label: 'Minutes' },
            { value: timeLeft.seconds, label: 'Seconds' }
          ].map((item, index) => (
            <div key={index} className="hero-countdown-item">
              <span className="hero-countdown-number">
                {String(item.value).padStart(2, '0')}
              </span>
              <span className="hero-countdown-label">{item.label}</span>
            </div>
          ))}
        </motion.div>
        </div>

        {/* CTA — hidden on mobile since the hamburger is the primary nav */}
        <motion.div
          variants={fadeUp}
          initial="hidden"
          animate="show"
          custom={7}
          className="hero-cta-row hidden sm:flex"
        >
          <button
            onClick={() => goTo('/events')}
            className="hero-cta-btn"
            data-testid="hero-venue-btn"
          >
            <MapPin className="inline-block w-4 h-4 mr-2" />
            {cms.ctaLabel}
          </button>
        </motion.div>
      </div>

      {/* Scroll indicator — hidden on mobile (single-screen hero) */}
      <motion.div
        className="hero-scroll-indicator hidden sm:block"
        animate={{ y: [0, 10, 0] }}
        transition={{ repeat: Infinity, duration: 2.4, ease: 'easeInOut' }}
        onClick={() => goTo('/our-story')}
        data-testid="hero-scroll-indicator"
      >
        <ChevronDown className="w-7 h-7" />
      </motion.div>
    </section>
  );
};

export { HeroSection };
